import { CheckCircle, Hash, MessageCircle, Plus, RefreshCw, X } from 'lucide-react';
import { useCallback, useState } from 'react';
import { SlackWizardConfig } from '../SlackWizard';
import { useSlackChannels } from '../hooks/useSlackChannels';

interface ConnectTargetStepProps {
	nodeId: string;
	config: SlackWizardConfig;
	onChange: (updates: Partial<SlackWizardConfig>) => void;
	errors: string[];
}

export function ConnectTargetStep({ nodeId, config, onChange, errors }: ConnectTargetStepProps) {
	const [newChannel, setNewChannel] = useState('');
	const [showChannelSelector, setShowChannelSelector] = useState(false);

	// Fetch available channels from Slack API
	const {
		channels: availableChannels,
		isLoading: channelsLoading,
		error: channelsError,
		refreshChannels,
	} = useSlackChannels();

	const handleCredentialChange = useCallback(
		(credentialId: string) => {
			onChange({ credentialId });
		},
		[onChange]
	);

	const handleAddChannel = useCallback(() => {
		if (newChannel.trim()) {
			const formattedChannel = newChannel.startsWith('#') ? newChannel : `#${newChannel}`;
			// Remove duplicates when adding manually
			const uniqueChannels = [...new Set([...config.channels, formattedChannel])];
			onChange({
				channels: uniqueChannels,
			});
			setNewChannel('');
		}
	}, [newChannel, config.channels, onChange]);

	const handleSelectChannel = useCallback(
		(channelId: string, channelName: string) => {
			const displayName = `#${channelName}`;
			if (!config.channels.includes(displayName)) {
				// Remove duplicates and add new channel
				const uniqueChannels = [...new Set([...config.channels, displayName])];
				onChange({
					channels: uniqueChannels,
				});
			}
			setShowChannelSelector(false);
		},
		[config.channels, onChange]
	);

	const handleRemoveChannel = useCallback(
		(index: number) => {
			onChange({
				channels: config.channels.filter((_, i) => i !== index),
			});
		},
		[config.channels, onChange]
	);

	const handleThreadingModeChange = useCallback(
		(mode: 'none' | 'reply' | 'start_new') => {
			onChange({
				threading: {
					...config.threading,
					mode,
					// Clear threadTs if switching away from reply mode
					threadTs: mode === 'reply' ? config.threading.threadTs : undefined,
				},
			});
		},
		[config.threading, onChange]
	);

	const handleThreadTsChange = useCallback(
		(threadTs: string) => {
			onChange({
				threading: {
					...config.threading,
					threadTs,
				},
			});
		},
		[config.threading, onChange]
	);

	const handleExposeThreadTs = useCallback(
		(exposeTs: boolean) => {
			onChange({
				threading: {
					...config.threading,
					exposeTs,
				},
			});
		},
		[config.threading, onChange]
	);

	return (
		<div className="space-y-6">
			<div>
				<h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
					Connect & Target Configuration
				</h3>
				<p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
					Configure your Slack bot credentials and target channels for message delivery.
				</p>
			</div>

			{/* Credential Selection */}
			<div className="space-y-3">
				<label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
					Slack Bot Credentials
				</label>
				<div className="relative">
					<select
						value={config.credentialId}
						onChange={e => handleCredentialChange(e.target.value)}
						className="block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
					>
						<option value="">Select Slack credentials...</option>
						<option value="default">Composer Bot Token</option>
						{/* Additional credentials can be added here in the future */}
					</select>
				</div>
				<p className="text-xs text-gray-500 dark:text-gray-400">
					Bot tokens provide access to post messages and interact with Slack channels.
				</p>
			</div>

			{/* Channel Selection */}
			<div className="space-y-3">
				<label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
					Target Channels
				</label>

				{/* Existing Channels */}
				<div className="space-y-2">
					{config.channels.map((channel, index) => (
						<div
							key={index}
							className="flex items-center justify-between px-3 py-2 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-md"
						>
							<div className="flex items-center">
								<Hash className="w-4 h-4 text-gray-400 mr-2" />
								<span className="text-sm text-gray-900 dark:text-white">
									{channel}
								</span>
							</div>
							<button
								type="button"
								onClick={() => handleRemoveChannel(index)}
								className="text-red-500 hover:text-red-700 p-1"
							>
								<X className="w-4 h-4" />
							</button>
						</div>
					))}
				</div>

				{/* Channel Selection Options */}
				<div className="space-y-3">
					<div className="flex space-x-2">
						<button
							type="button"
							onClick={() => setShowChannelSelector(!showChannelSelector)}
							className="flex-1 inline-flex items-center justify-center px-3 py-2 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
						>
							<Hash className="w-4 h-4 mr-2" />
							Select from Available Channels
						</button>
						<button
							type="button"
							onClick={refreshChannels}
							disabled={channelsLoading}
							className="inline-flex items-center px-3 py-2 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
						>
							<RefreshCw
								className={`w-4 h-4 ${channelsLoading ? 'animate-spin' : ''}`}
							/>
						</button>
					</div>

					{/* Available Channels Dropdown */}
					{showChannelSelector && (
						<div className="border border-gray-200 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 max-h-48 overflow-y-auto">
							{channelsLoading && (
								<div className="flex items-center justify-center py-4">
									<RefreshCw className="w-4 h-4 animate-spin text-gray-400 mr-2" />
									<span className="text-sm text-gray-500 dark:text-gray-400">
										Loading channels...
									</span>
								</div>
							)}

							{channelsError && (
								<div className="p-3 text-sm text-red-600 dark:text-red-400">
									Error loading channels: {channelsError}
								</div>
							)}

							{!channelsLoading &&
								!channelsError &&
								availableChannels.length === 0 && (
									<div className="p-3 text-sm text-gray-500 dark:text-gray-400">
										No channels found. Make sure the bot is invited to channels.
									</div>
								)}

							{!channelsLoading &&
								!channelsError &&
								availableChannels.map(channel => {
									const isSelected = config.channels.includes(
										channel.displayName
									);
									return (
										<div
											key={channel.id}
											className={`flex items-center justify-between px-3 py-2 hover:bg-gray-50 dark:hover:bg-gray-600 cursor-pointer ${
												isSelected ? 'bg-blue-50 dark:bg-blue-900/20' : ''
											}`}
											onClick={() =>
												handleSelectChannel(channel.id, channel.name)
											}
										>
											<div className="flex items-center">
												<Hash className="w-4 h-4 text-gray-400 mr-2" />
												<span className="text-sm text-gray-900 dark:text-white">
													{channel.displayName}
												</span>
												{channel.is_private && (
													<span className="ml-2 text-xs text-gray-500 dark:text-gray-400">
														(private)
													</span>
												)}
											</div>
											{isSelected && (
												<CheckCircle className="w-4 h-4 text-blue-600 dark:text-blue-400" />
											)}
										</div>
									);
								})}
						</div>
					)}

					{/* Manual Channel Input */}
					<div className="text-xs text-gray-500 dark:text-gray-400 text-center">or</div>

					<div className="flex space-x-2">
						<div className="flex-1 relative">
							<Hash className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
							<input
								type="text"
								value={newChannel}
								onChange={e => setNewChannel(e.target.value)}
								onKeyPress={e => {
									if (e.key === 'Enter') {
										e.preventDefault();
										handleAddChannel();
									}
								}}
								placeholder="Enter channel name manually"
								className="block w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
							/>
						</div>
						<button
							type="button"
							onClick={handleAddChannel}
							disabled={!newChannel.trim()}
							className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
						>
							<Plus className="w-4 h-4" />
						</button>
					</div>
				</div>

				<p className="text-xs text-gray-500 dark:text-gray-400">
					Select from available channels or enter channel names manually. You can target
					multiple channels.
				</p>
			</div>

			{/* Threading Options */}
			<div className="space-y-3">
				<label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
					Threading Options
				</label>

				<div className="space-y-3">
					<div className="flex items-center">
						<input
							type="radio"
							id="thread-none"
							name="threading-mode"
							value="none"
							checked={config.threading.mode === 'none'}
							onChange={() => handleThreadingModeChange('none')}
							className="focus:ring-blue-500 h-4 w-4 text-blue-600 border-gray-300 dark:border-gray-600"
						/>
						<label
							htmlFor="thread-none"
							className="ml-3 block text-sm text-gray-900 dark:text-white"
						>
							Post as new message (no threading)
						</label>
					</div>

					<div className="flex items-center">
						<input
							type="radio"
							id="thread-reply"
							name="threading-mode"
							value="reply"
							checked={config.threading.mode === 'reply'}
							onChange={() => handleThreadingModeChange('reply')}
							className="focus:ring-blue-500 h-4 w-4 text-blue-600 border-gray-300 dark:border-gray-600"
						/>
						<label
							htmlFor="thread-reply"
							className="ml-3 block text-sm text-gray-900 dark:text-white"
						>
							Reply to existing thread
						</label>
					</div>

					<div className="flex items-center">
						<input
							type="radio"
							id="thread-start"
							name="threading-mode"
							value="start_new"
							checked={config.threading.mode === 'start_new'}
							onChange={() => handleThreadingModeChange('start_new')}
							className="focus:ring-blue-500 h-4 w-4 text-blue-600 border-gray-300 dark:border-gray-600"
						/>
						<label
							htmlFor="thread-start"
							className="ml-3 block text-sm text-gray-900 dark:text-white"
						>
							Start new thread and expose timestamp
						</label>
					</div>
				</div>

				{/* Thread Timestamp Input (for reply mode) */}
				{config.threading.mode === 'reply' && (
					<div className="mt-3">
						<label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
							Thread Timestamp
						</label>
						<input
							type="text"
							value={config.threading.threadTs || ''}
							onChange={e => handleThreadTsChange(e.target.value)}
							placeholder="e.g., {{previous_message.ts}} or 1234567890.123456"
							className="block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
						/>
						<p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
							Use tokens like {'{{previous_message.ts}}'} or provide a specific
							timestamp.
						</p>
					</div>
				)}

				{/* Expose Thread Timestamp (for start_new mode) */}
				{config.threading.mode === 'start_new' && (
					<div className="mt-3">
						<div className="flex items-center">
							<input
								type="checkbox"
								id="expose-thread-ts"
								checked={config.threading.exposeTs || false}
								onChange={e => handleExposeThreadTs(e.target.checked)}
								className="focus:ring-blue-500 h-4 w-4 text-blue-600 border-gray-300 dark:border-gray-600 rounded"
							/>
							<label
								htmlFor="expose-thread-ts"
								className="ml-3 block text-sm text-gray-900 dark:text-white"
							>
								Expose thread timestamp for downstream nodes
							</label>
						</div>
						<p className="text-xs text-gray-500 dark:text-gray-400 mt-1 ml-7">
							Make the new thread timestamp available as {'{{slack.thread_ts}}'} for
							subsequent nodes.
						</p>
					</div>
				)}
			</div>

			{/* Error Display */}
			{errors.length > 0 && (
				<div className="rounded-md bg-red-50 dark:bg-red-900/20 p-4">
					<div className="flex">
						<MessageCircle className="h-5 w-5 text-red-400" />
						<div className="ml-3">
							<h3 className="text-sm font-medium text-red-800 dark:text-red-200">
								Configuration Issues
							</h3>
							<div className="mt-2 text-sm text-red-700 dark:text-red-300">
								<ul className="list-disc list-inside space-y-1">
									{errors.map((error, index) => (
										<li key={index}>{error}</li>
									))}
								</ul>
							</div>
						</div>
					</div>
				</div>
			)}
		</div>
	);
}
